import {
  Dataset,
  LoginActivity,
  ProductEventActivity,
  Series,
} from '@/components/Graphs'
import { BenchmarkStatsProps } from '@/components/Pages/ThreatMitigation/Prevent'
import {
  AlertStatusHistogram,
  RuleTriggerHistogram,
} from '@/services/partner/organization'
import { formatToLocalComputerDate } from '@/utils/methods'
import { BenchmarkDriftHistory } from '@saasalerts/common/fortify'
import { ApplicationName } from '@saasalerts/common/shared'
import { orderBy } from 'lodash'
import moment from 'moment'

export const CHART_COLORS = {
  brand: '#57893b',
  gray: '#6B7280',
  red: '#EF4444',
  amber: '#F59E0B',
  blueGray: '#64748B',
  yellow: '#F59E0B',
  lime: '#84CC16',
  teal: '#14B8A6',
  violet: '#8B5CF6',
  green: '#10B981',
  warmGray: '#78716C',
  indigo: '#6366F1',
  cyan: '#06B6D4',
  blue: '#3B82F6',
  purple: '#8B5CF6',
  fuchsia: '#D946EF',
  pink: '#EC4899',
  rose: '#F43F5E',
}

export const DONUT_CHART_COLORS = {
  red: [
    '#7F1D1D',
    '#991B1B',
    '#B91C1C',
    '#DC2626',
    '#EF4444',
    '#F87171',
    '#FCA5A5',
    '#FECACA',
    '#FEE2E2',
    '#FEF2F2',
  ],
  yellow: [
    '#713F12',
    '#854D0E',
    '#A16207',
    '#CA8A04',
    '#EAB308',
    '#FACC15',
    '#FDE047',
    '#FEF08A',
    '#FEF9C3',
    '#FEFCE8',
  ],
  green: [
    '#14532D',
    '#166534',
    '#15803D',
    '#16A34A',
    '#22C55E',
    '#4ADE80',
    '#86EFAC',
    '#BBF7D0',
    '#DCFCE7',
    '#F0FDF4',
  ],
}

export const ALL_COLORS = Object.values(CHART_COLORS).sort(
  () => Math.random() - 0.5
)

export const getUniqueProducts = (
  productEventActivities: Array<ProductEventActivity>
): ApplicationName[] => {
  const set = new Set<ApplicationName>()
  productEventActivities.forEach((row) => {
    set.add(row.product as ApplicationName)
  })

  return Array.from(set)
}

export function productEventActivityToLineChartData(
  productEventActivities: ProductEventActivity[] | undefined
) {
  if (!productEventActivities) {
    return { dataset: [], series: [] }
  }
  const dataset: {
    dimensions: string[]
    source: { label: string; count: number }[]
  }[] = []
  const series: {
    type: 'line'
    datasetIndex: number
    name: string
  }[] = []
  productEventActivities.forEach((productBucket, index) => {
    dataset.push({
      dimensions: ['label', 'count'],
      source: productBucket.dates,
    })
    series.push({
      type: 'line',
      datasetIndex: index,
      name: productBucket.product,
    })
  })

  return { dataset, series }
}

export function loginActivityToLineChartData(
  loginActivities: LoginActivity[] | undefined
) {
  if (!loginActivities) {
    return { dataset: [], series: [] }
  }
  const dataset: {
    dimensions: string[]
    source: { label: string; count: number }[]
  }[] = []
  const series: {
    type: 'line'
    datasetIndex: number
    name: string
  }[] = []

  const successData = loginActivities.map((row) => ({
    label: row.date,
    count: row.success,
  }))
  const failData = loginActivities.map((row) => ({
    label: row.date,
    count: row.fail,
  }))

  dataset.push(
    {
      dimensions: ['label', 'count'],
      source: successData,
    },
    {
      dimensions: ['label', 'count'],
      source: failData,
    }
  )
  series.push(
    {
      type: 'line',
      datasetIndex: 0,
      name: 'Success',
    },
    {
      type: 'line',
      datasetIndex: 1,
      name: 'Fail',
    }
  )

  return { dataset, series }
}

export function RuleTriggerHistogramDataToLineChartData(
  ruleTriggers: RuleTriggerHistogram[] | undefined
) {
  if (!ruleTriggers) {
    return { dataset: [], series: [] }
  }
  const dataset: {
    dimensions: string[]
    source: { label: string; count: number }[]
  }[] = []
  const series: {
    type: 'line'
    datasetIndex: number
    name: string
  }[] = []

  const ignoredData = ruleTriggers.map((row) => ({
    label: row.date,
    count: row.ignored,
  }))
  const autoIgnoredData = ruleTriggers.map((row) => ({
    label: row.date,
    count: row.autoIgnored,
  }))

  const approvedData = ruleTriggers.map((row) => ({
    label: row.date,
    count: row.approved,
  }))

  const autoApprovedData = ruleTriggers.map((row) => ({
    label: row.date,
    count: row.autoApproved,
  }))

  const failedData = ruleTriggers.map((row) => ({
    label: row.date,
    count: row.failed,
  }))
  const remediationCompletedData = ruleTriggers.map((row) => ({
    label: row.date,
    count: row.remediationCompleted,
  }))

  dataset.push(
    {
      dimensions: ['label', 'count'],
      source: ignoredData,
    },
    {
      dimensions: ['label', 'count'],
      source: autoIgnoredData,
    },
    {
      dimensions: ['label', 'count'],
      source: approvedData,
    },
    {
      dimensions: ['label', 'count'],
      source: autoApprovedData,
    },
    {
      dimensions: ['label', 'count'],
      source: failedData,
    },
    {
      dimensions: ['label', 'count'],
      source: remediationCompletedData,
    }
  )
  series.push(
    {
      type: 'line',
      datasetIndex: 0,
      name: 'Ignored',
    },
    {
      type: 'line',
      datasetIndex: 1,
      name: 'Auto Ignored',
    },
    {
      type: 'line',
      datasetIndex: 2,
      name: 'Approved',
    },
    {
      type: 'line',
      datasetIndex: 3,
      name: 'Auto Approved',
    },
    {
      type: 'line',
      datasetIndex: 4,
      name: 'Failed',
    },
    {
      type: 'line',
      datasetIndex: 5,
      name: 'Remediation Completed',
    }
  )

  return { dataset, series }
}

export function totalEventActivityToLineChartData(
  events: AlertStatusHistogram[]
) {
  if (!events) {
    return { dataset: [], series: [] }
  }
  const dataset: {
    dimensions: string[]
    source: { label: string; count: number }[]
  }[] = []

  const series: {
    type: 'line'
    datasetIndex: number
    name: string
    color: string
  }[] = []

  const lowAlertData = events.map((row) => ({
    label: row.date,
    count: row.low,
  }))

  const mediumAlertData = events.map((row) => ({
    label: row.date,
    count: row.medium,
  }))

  const criticalAlertData = events.map((row) => ({
    label: row.date,
    count: row.critical,
  }))

  dataset.push(
    {
      dimensions: ['label', 'count'],
      source: lowAlertData,
    },
    {
      dimensions: ['label', 'count'],
      source: mediumAlertData,
    },
    {
      dimensions: ['label', 'count'],
      source: criticalAlertData,
    }
  )
  series.push(
    {
      type: 'line',
      datasetIndex: 0,
      name: 'Low',
      color: '#059669',
    },
    {
      type: 'line',
      datasetIndex: 1,
      name: 'Medium',
      color: '#f59e0b',
    },
    {
      type: 'line',
      datasetIndex: 2,
      name: 'Critical',
      color: '#dc2626',
    }
  )
  return { dataset, series }
}

export function statsHistoryLineChartData(stats?: BenchmarkStatsProps | null) {
  if (!stats) {
    return { dataset: [], series: [] }
  }
  const dataset: {
    dimensions: string[]
    source: { label: string; count: number }[]
  }[] = []

  const series: {
    type: 'line'
    datasetIndex: number
    name: string
    color: string
  }[] = []

  const partnerBenchmarkHistory = stats?.allPartnerBenchmarkHistory
    ?.filter((_, index, benchmarkHistory) => {
      /*
       * Threat Mitigation report History Graph showing 0 for current day
       * Skip the last element if its count is 0
       */
      if (index === benchmarkHistory.length - 1) {
        const lastBenchmark = benchmarkHistory[index]
        const count =
          lastBenchmark.secureScoreBenchmark?.allPartners?.average ?? 0
        return count !== 0
      }
      return true
    })
    ?.map((score) => ({
      ...score,
      date: formatToLocalComputerDate(score.date),
      count: score.secureScoreBenchmark?.allPartners?.average ?? 0,
    }))

  const orgSecureScoreBenchmarkHistory = stats?.secureScoreBenchmarkHistory
    ?.filter((_, index, scoreBenchmarkHistory) => {
      /*
       * Threat Mitigation report History Graph showing 0 for current day
       * Skip the last element if its count is 0
       */
      if (index === scoreBenchmarkHistory.length - 1) {
        const lastBenchmark = scoreBenchmarkHistory[index]
        const count = lastBenchmark.secureScorePercent ?? 0
        return count !== 0
      }
      return true
    })
    ?.map((score) => ({
      ...score,
      date: formatToLocalComputerDate(score.date),
      count: score.secureScorePercent ?? 0,
    }))

  const HistoryLineChart = [
    {
      name: 'Benchmark',
      color: '#FAC858',
      data: partnerBenchmarkHistory,
    },
    {
      name: 'Organization Secure Score',
      color: '#91cc75',
      data: orgSecureScoreBenchmarkHistory,
    },
  ]

  HistoryLineChart?.forEach((lineChart, index) => {
    const source = lineChart?.data?.map((score) => {
      return {
        label: score.date,
        count: Number(score.count?.toFixed(2)) ?? 0,
      }
    })
    dataset.push({
      dimensions: ['label', 'count'],
      source: orderBy(source, ['label'], ['asc']),
    })
    series.push({
      type: 'line',
      datasetIndex: index,
      name: lineChart.name,
      color: lineChart.color,
    })
  })

  return { dataset, series }
}

export const getChartConfiguration = (data: BenchmarkDriftHistory[]) => {
  const dataset: Dataset[] = []
  const series: Series[] = []

  const source =
    data?.map((score) => {
      const count =
        score.totalItems > 0
          ? Number(((score.appliedItems * 100) / score.totalItems).toFixed(2))
          : 0
      const label = moment(score.timestamp).format('YYYY-MM-DD')
      return { label, count }
    }) ?? []

  dataset.push({
    dimensions: ['label', 'count'],
    source: orderBy(source, ['label'], ['asc']),
  })

  series.push({
    type: 'line',
    smooth: true,
    symbol: 'none',
    name: 'Score', // matches the Y-axis meaning
    color: '#91cc75',
  })

  return { dataset, series }
}

export const getLineChartOption = (data: BenchmarkDriftHistory[]) => {
  const config = getChartConfiguration(data)
  const source = (config.dataset?.[0]?.source ?? []) as Array<{
    label: string
    count: number
  }>

  return {
    dataset: config.dataset,
    series: config.series,
    legend: {
      show: false,
    },
    grid: {
      top: 20,
      bottom: 20,
      left: 0,
      right: 0,
    },
    xAxis: {
      type: 'category' as const,
    },
    yAxis: {
      type: 'value' as const,
      min: 0,
      max: 100,
      axisLabel: {
        formatter: (value: number) => `${value}%`,
      },
    },
    tooltip: {
      trigger: 'axis' as const,
      formatter: (params: any) => {
        const point = params[0]
        const originalValue = source[point.dataIndex]
        return `${originalValue.label}: ${originalValue.count}%`
      },
    },
  }
}

export const getFilterByDateRange = (
  data: BenchmarkDriftHistory[],
  startDate?: string,
  endDate?: string
) => {
  // If both dates are missing, return all data
  if (!startDate && !endDate) {
    return data
  }

  // Handle start date if provided
  const startDateTime = startDate
    ? new Date(`${startDate}T00:00:00.000Z`)
    : null

  // Handle end date if provided
  const endDateTime = endDate ? new Date(`${endDate}T23:59:59.999Z`) : null

  return data.filter((item) => {
    const itemDate = new Date(item.timestamp)

    // Check against start date if provided
    const isAfterStart = startDateTime ? itemDate >= startDateTime : true

    // Check against end date if provided
    const isBeforeEnd = endDateTime ? itemDate <= endDateTime : true

    return isAfterStart && isBeforeEnd
  })
}
